import { useCallback, useEffect, useRef, useState } from 'react';

interface UseScrollBasedVisibilityParams {
    minViewportHeight: number; // Show immediately if viewport >= this height
    minScrollAmount: number; // Show after scrolling this many pixels on small viewports
    enabled?: boolean; // Allow disabling the feature entirely
    bottomThreshold?: number; // Distance from bottom (in pixels) to trigger hide - default 50px
    nonScrollableDelay?: number; // Delay (in ms) before showing on non-scrollable pages - default 4000ms
}

interface ScrollVisibilityState {
    shouldShowWidget: boolean;
    triggeredByScroll: boolean;
    viewportHeight: number;
    scrollPosition: number;
    isNearBottom: boolean;
    isScrollingUp: boolean;
    isScrollingDown: boolean;
}

/**
 * Hook to control widget visibility based on viewport height and scroll position.
 *
 * Enhanced logic:
 * - If viewport height >= minViewportHeight: show immediately
 * - If viewport height < minViewportHeight:
 *   - Check if page can scroll (scrollHeight > innerHeight)
 *   - If page cannot scroll: show after delay (nonScrollableDelay)
 *   - If page can scroll: hide until scroll > minScrollAmount
 * - Once shown via scroll: stay visible (persist)
 * - Bottom detection: hide widget when near page bottom (unless scrolling up)
 * - Scroll direction tracking for smooth show/hide animations
 *
 * @param params Configuration parameters
 * @returns Visibility state including scroll direction and bottom detection
 */
export const useScrollBasedVisibility = ({
    minViewportHeight,
    minScrollAmount,
    enabled = true,
    bottomThreshold = 50,
    nonScrollableDelay = 4000,
}: UseScrollBasedVisibilityParams): ScrollVisibilityState => {
    const [shouldShowWidget, setShouldShowWidget] = useState(false);
    const [triggeredByScroll, setTriggeredByScroll] = useState(false);
    const [viewportHeight, setViewportHeight] = useState(0);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [hasBeenShown, setHasBeenShown] = useState(false);
    const [isNearBottom, setIsNearBottom] = useState(false);
    const [isScrollingUp, setIsScrollingUp] = useState(false);
    const [isScrollingDown, setIsScrollingDown] = useState(false);

    // Track previous scroll position for direction detection
    const previousScrollYRef = useRef<number>(0);
    const delayTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Check if page can scroll
    const canPageScroll = useCallback((): boolean => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return false;

        const docElementScrollHeight = document.documentElement.scrollHeight;
        const bodyScrollHeight = document.body?.scrollHeight || 0;
        const innerHeight = window.innerHeight;

        // Check both document.documentElement and document.body
        const canScroll = Math.max(docElementScrollHeight, bodyScrollHeight) > innerHeight;

        return canScroll;
    }, []);

    // Get current scroll position
    const getScrollPosition = useCallback((): number => {
        if (typeof window === 'undefined') return 0;
        return window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    }, []);

    // Determine if widget should be visible
    const checkVisibility = useCallback(() => {
        if (typeof window === 'undefined') return;

        const currentViewportHeight = window.innerHeight;
        const currentScrollPosition = getScrollPosition();
        const pageCanScroll = canPageScroll();

        // Calculate scroll direction
        const previousScrollY = previousScrollYRef.current;
        const scrollingUp = currentScrollPosition < previousScrollY;
        const scrollingDown = currentScrollPosition > previousScrollY;
        previousScrollYRef.current = currentScrollPosition;

        // Calculate distance to bottom
        const scrollHeight = Math.max(
            document.documentElement.scrollHeight,
            document.body?.scrollHeight || 0
        );
        const distanceToBottom = scrollHeight - (currentScrollPosition + currentViewportHeight);
        const nearBottom = distanceToBottom < bottomThreshold && currentScrollPosition > 0;

        setViewportHeight(currentViewportHeight);
        setScrollPosition(currentScrollPosition);
        setIsScrollingUp(scrollingUp);
        setIsScrollingDown(scrollingDown);
        setIsNearBottom(nearBottom);

        // If disabled, always show
        if (!enabled) {
            setShouldShowWidget(true);
            return;
        }

        // If already shown via scroll, keep it visible
        if (hasBeenShown) {
            setShouldShowWidget(true);
            return;
        }

        // If viewport is large enough, show immediately
        if (currentViewportHeight >= minViewportHeight) {
            setShouldShowWidget(true);
            setHasBeenShown(true); // Persist visibility on resize
            return;
        }

        // Viewport is small - check if page can scroll
        if (!pageCanScroll) {
            // Page cannot scroll - show after delay to give user time to read
            if (!hasBeenShown) {
                // Clear any existing timer
                if (delayTimerRef.current) {
                    clearTimeout(delayTimerRef.current);
                }

                // Set timer to show widget after delay
                delayTimerRef.current = setTimeout(() => {
                    setShouldShowWidget(true);
                    setHasBeenShown(true);
                }, nonScrollableDelay);

                return;
            } else {
                // Widget already shown, keep it visible
                setShouldShowWidget(true);
                return;
            }
        }

        // Viewport is small and page can scroll - check scroll position
        if (currentScrollPosition >= minScrollAmount) {
            setShouldShowWidget(true);
            setTriggeredByScroll(true);
            setHasBeenShown(true); // Persist visibility
            return;
        }

        // Viewport is small, page can scroll, but user hasn't scrolled enough
        setShouldShowWidget(false);
    }, [
        enabled,
        minViewportHeight,
        minScrollAmount,
        hasBeenShown,
        canPageScroll,
        getScrollPosition,
        bottomThreshold,
        nonScrollableDelay,
    ]);

    // Initial check and setup listeners
    useEffect(() => {
        checkVisibility();

        // Set up scroll and resize listeners
        const handleScroll = () => {
            checkVisibility();
        };

        const handleResize = () => {
            checkVisibility();
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);

            // Cleanup delay timer on unmount
            if (delayTimerRef.current) {
                clearTimeout(delayTimerRef.current);
            }
        };
    }, [checkVisibility]);

    return {
        shouldShowWidget,
        triggeredByScroll,
        viewportHeight,
        scrollPosition,
        isNearBottom,
        isScrollingUp,
        isScrollingDown,
    };
};
